'use client';
// Dossier de mise en service d'un équipement (K1) — bloc de l'onglet « Présentation »
// (spec 002, récit 9) : en tête tant que l'appareil n'est pas autorisé, replié en bas ensuite.
//
// Check-list PERSISTANTE, pas un tunnel : elle s'ouvre à l'arrivée de
// l'appareil et se coche pendant des semaines, au rythme des livraisons et des
// interventions du fournisseur. Le serveur (`mes.ts`) est seul juge de ce qui
// est autorisable — cet écran affiche et propose, il ne décide rien.
import React, { useState } from 'react';
import { useGed } from '@/lib/store';
import { I, card, inputStyle, EmptyState, ConfirmButton, useBusy } from '@/components/ui';
import { lbl } from './commun';
import { Norme } from '../../Norme';
import { avancement } from '@/shared/miseEnService';
import type { GedEquipement, EtapeMES } from '@/lib/types';
import { fmtDate } from '@/lib/meta';

import { Bouton } from '@/shared/Bouton';
import { peutGererParc } from '@/lib/droits-parc';

/* Le parcours d'accueil est devenu le bandeau de la Présentation, et la liste
   « Contacts » saisie à la main est lue dans le bloc Contacts unique, relié à
   l'annuaire (`ContactsEquipement.tsx`, spec 002, récit 9) : rien n'en est effacé. */
export function MiseEnService({ e }: { e: GedEquipement }) {
  const { session, smq } = useGed();
  const isRQ = session.roles.includes('rq'); // autoriser la mise en service reste au RQ
  const peutParc = peutGererParc(session.roles);
  const d = e.miseEnService || null;
  const { run, estOccupe } = useBusy();

  if (!d?.etapes?.length) {
    return (
      <div style={{ display: 'grid', gap: 14 }}>
        <EmptyState
          titre="Aucun dossier de mise en service."
          indice="Le dossier suit l’appareil de l’expression du besoin à l’autorisation d’usage — douze étapes par défaut, modifiables au fil du dossier." />
        {peutParc && (
          <Bouton variante="primaire" taille="sm" onClick={() => void run('ouvrir', () => smq(`ged/equip/${e.documentId}/mes`, {}, 'Dossier de mise en service ouvert.'))} disabled={estOccupe()} style={{ width: 'fit-content' }}>
            {estOccupe('ouvrir') ? 'Ouverture…' : 'Ouvrir le dossier de mise en service'}
          </Bouton>
        )}
      </div>
    );
  }

  const av = avancement(d);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <Resume d={d} av={av} isRQ={isRQ} equipId={e.documentId} />
      <div>
        <div style={lbl}>Étapes</div>
        <div style={{ display: 'grid', gap: 8 }}>
          {[...d.etapes].sort((a, b) => a.ordre - b.ordre).map((et) => (
            <Etape key={et.id} et={et} equipId={e.documentId} verrouille={!!d.autoriseLe} />
          ))}
        </div>
      </div>
    </div>
  );
}

function Resume({ d, av, isRQ, equipId }: {
  d: NonNullable<GedEquipement['miseEnService']>; av: ReturnType<typeof avancement>; isRQ: boolean; equipId: string;
}) {
  const { smq } = useGed();
  if (d.autoriseLe) {
    return (
      <div style={{ ...card, padding: 14, background: 'var(--green-50)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <I n="check" s={16} />
        <span style={{ fontSize: 12.5 }}>
          <strong>Mise en service autorisée</strong> le {fmtDate(d.autoriseLe)} — {av.faites}/{av.total} étapes franchies
          {av.ecartees ? `, ${av.ecartees} écartée(s)` : ''}.
        </span>
      </div>
    );
  }
  return (
    <div style={{ ...card, padding: 14, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
      <div style={{ flex: '1 1 220px' }}>
        <div style={{ height: 8, borderRadius: 'var(--radius-pill)', background: 'var(--surface-sunken)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${av.total ? Math.round((av.faites / av.total) * 100) : 0}%`, background: 'var(--color-primary)' }} />
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 5 }}>
          {av.faites}/{av.total} étapes franchies
          {av.ecartees ? ` · ${av.ecartees} écartée(s)` : ''}
          {av.bloquantesDues.length ? ` · ${av.bloquantesDues.length} bloquante(s) restante(s)` : ''}
        </div>
      </div>
      {isRQ && (
        <ConfirmButton
          label="Autoriser la mise en service" icon="check" variant="primary"
          disabled={!av.autorisable}
          titre={!av.autorisable
            ? `Étape(s) bloquante(s) non franchie(s) : ${av.bloquantesDues.map((x) => `${x.ordre}. ${x.libelle}`).join(' — ')}`
            : undefined}
          question="Autoriser la mise en service ? L’équipement passera « en service » et pourra rendre des résultats patients."
          onConfirm={() => smq(`ged/equip/${equipId}/mes/autoriser`, {}, 'Mise en service autorisée.')} />
      )}
    </div>
  );
}

function Etape({ et, equipId, verrouille }: { et: EtapeMES; equipId: string; verrouille: boolean }) {
  const { smq } = useGed();
  const [ecarter, setEcarter] = useState(false);
  const [motif, setMotif] = useState(et.motifSansObjet || '');
  const [erreurMotif, setErreurMotif] = useState('');
  const [champs, setChamps] = useState({ responsable: et.responsable || '', echeance: et.echeance || '', note: et.note || '' });
  const [modifie, setModifie] = useState(false);
  const { run, estOccupe } = useBusy();
  const busyKey = `etape-${et.id}`;

  const maj = (body: Record<string, unknown>) => run(busyKey, () => smq(`ged/equip/${equipId}/mes/etape`, { etapeId: et.id, ...body }));

  return (
    <div style={{
      ...card, padding: 12, display: 'grid', gap: 8,
      opacity: et.sansObjet ? 0.6 : 1,
      borderColor: et.bloquante && !et.fait && !et.sansObjet ? 'var(--amber-300)' : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <input type="checkbox" checked={!!et.fait} disabled={verrouille || et.sansObjet || estOccupe(busyKey)}
          onChange={(ev) => void maj({ fait: ev.target.checked })}
          style={{ marginTop: 3, width: 16, height: 16 }} aria-label={`Étape ${et.ordre} faite`} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}>{et.ordre}.</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', textDecoration: et.sansObjet ? 'line-through' : undefined }}>{et.libelle}</span>
            {et.art && <Norme art={et.art} />}
            {et.bloquante && !et.fait && !et.sansObjet && (
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)' }}>bloquante</span>
            )}
          </div>
          {et.piece && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>À montrer : {et.piece}</div>}
          {et.sansObjet && et.motifSansObjet && (
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3, fontStyle: 'italic' }}>Écartée — {et.motifSansObjet}</div>
          )}
        </div>
      </div>

      {!verrouille && !et.fait && (
        <div>
          {!ecarter
            ? <Bouton taille="sm" onClick={() => setEcarter(true)}>
              {et.sansObjet ? 'Modifier le motif' : 'Sans objet pour cet équipement'}
            </Bouton>
            : (
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <textarea value={motif} onChange={(ev) => { setMotif(ev.target.value); if (erreurMotif) setErreurMotif(''); }}
                  placeholder="Motif (≥ 10 caractères, lu en audit)" rows={1}
                  style={{ ...inputStyle, flex: '1 1 220px', fontSize: 12.5, borderColor: erreurMotif ? 'var(--red-600)' : undefined }} />
                <Bouton taille="sm" onClick={() => void run(busyKey, async () => {
                  if (motif.trim().length < 10) { setErreurMotif('Au moins 10 caractères.'); return; }
                  await maj({ sansObjet: true, motifSansObjet: motif.trim() });
                  setEcarter(false);
                })} disabled={estOccupe(busyKey)}>Écarter</Bouton>
                <Bouton taille="sm" onClick={() => setEcarter(false)}>Annuler</Bouton>
              </div>
            )}
          {erreurMotif && <div style={{ fontSize: 12.5, color: 'var(--red-600)', marginTop: 3 }}>{erreurMotif}</div>}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 2fr auto', gap: 6, alignItems: 'center' }}>
        <input value={champs.responsable} disabled={verrouille} placeholder="Responsable"
          onChange={(ev) => { setChamps({ ...champs, responsable: ev.target.value }); setModifie(true); }}
          style={{ ...inputStyle, fontSize: 12.5, padding: '5px 8px' }} />
        <input type="date" value={champs.echeance} disabled={verrouille}
          onChange={(ev) => { setChamps({ ...champs, echeance: ev.target.value }); setModifie(true); }}
          style={{ ...inputStyle, fontSize: 12.5, padding: '5px 8px' }} />
        <input value={champs.note} disabled={verrouille} placeholder="Note"
          onChange={(ev) => { setChamps({ ...champs, note: ev.target.value }); setModifie(true); }}
          style={{ ...inputStyle, fontSize: 12.5, padding: '5px 8px' }} />
        {!verrouille && modifie && (
          <Bouton variante="primaire" taille="sm" onClick={() => void run(busyKey, async () => { await maj(champs); setModifie(false); })} disabled={estOccupe(busyKey)}>
            {estOccupe(busyKey) ? '…' : 'Enregistrer'}
          </Bouton>
        )}
      </div>
    </div>
  );
}
